import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import pageStyles from '../styles/page-common.module.css';
import treeStyles from '../styles/page-tree.module.css';
import { stateResetTree, stateSetCurrentLanguage } from '../store';
import Loading from '../components/Loading';
import {ReactComponent as IconLeaf} from '../icons/logo_icononly.svg';
import {ReactComponent as IconTree0} from '../icons/icon_tree0.svg';
import {ReactComponent as IconTree1} from '../icons/icon_tree1.svg';
import {ReactComponent as IconTree2} from '../icons/icon_tree2.svg';
import {ReactComponent as IconTree3} from '../icons/icon_tree3.svg';
import {ReactComponent as IconTree4} from '../icons/icon_tree4.svg';
import {ReactComponent as IconTree5} from '../icons/icon_tree5.svg';
import {ReactComponent as IconTree6} from '../icons/icon_tree6.svg';
import {ReactComponent as IconTree7} from '../icons/icon_tree7.svg';
import { LEVEL_NAMES, LANGUAGE_NAMES, EARLY_CONTENT_WARNING_WEEK } from '../constants';
import { getTree } from '../backend';


function LeafButton({ index, tree, leaf, complete, score }) {
  return (
    <NavLink to={`/leaf/${tree._id}/${index}`} state={{ 'tree': tree }} style={{display: "contents"}}>
      <div className={treeStyles.leafBox}>
        <div>
          <IconLeaf className={complete ? treeStyles.leafIconActive : treeStyles.leafIconInactive}/>
        </div>
        <div className={treeStyles.leafText}>
          <div lang={tree.language} translate="no" className={`notranslate ${treeStyles.leafTitle}`}>{index + 1}. {leaf.title}</div>
          {tree.language === 'ja' && <div lang={tree.language} translate="no" className={`notranslate ${treeStyles.kanaLeafTitle}`}>({leaf.title_kana})</div>}
          <div>{complete ? `Leaf Collected! Score: ${score}` : ''}</div>
        </div>
      </div>
    </NavLink>
  );
}


// main page component
function Tree() {

  // get tree ID from URL parameters
  const { treeId } = useParams();

  const user = useSelector((state) => state.user);

  // // get tree from passed state
  // const location = useLocation();
  // const { treeMetadata } = location.state

  const [tree, setTree] = useState(null);
  const [levellangString, setLevelLangString] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // on mount, set user's currentLanguage to match tree's language and download tree
  useEffect(() => {
    async function loadTree() {
      setTree(null);
      try {
        setLoading(true);
        const response = await getTree({tree_id: treeId});
        setTree(response);
        setError("");
      }
      catch(error) {
        if (error.response && error.response.status=== 404) {
          setError("Tree not found.")
        }
        else {
          setError("Some error occurred.")
        }
      }
      finally {
        setLoading(false);
      }
    }

    if (treeId) {
      loadTree();
    }
  }, [treeId, user])

  // when tree is loaded, set current language and configure levelLangString
  useEffect(() => {
    if (tree) {
      stateSetCurrentLanguage(tree.language);
      setLevelLangString(`${LEVEL_NAMES[tree.level]} ${LANGUAGE_NAMES[tree.language]} #${tree.release_index}`);
    }
  }, [tree]);

  // get records data from redux state
  const treeRecords = useSelector((state) => state.treeRecords);
  
  // check if record exists
  const recordExists = treeRecords.hasOwnProperty(treeId);

  // if record exists, get leaf completions and scores
  let treeComplete = false;
  const completions = Array(7).fill(false);
  const scores = Array(7).fill("");
  if (recordExists) {
    const treeRecord = treeRecords[treeId]
    if (treeRecord.complete) {
      treeComplete = true;
    }
    for (let i = 0; i <= 6; i++) {
      if (treeRecord.leaves[i] && treeRecord.leaves[i].complete) {
        completions[i] = true;
        scores[i] = `${treeRecord.leaves[i].score}/5`
      }
    }
  }

  // work out current progress summary for banner and which icon to use
  let bannerContent;
  let icon;
  if (recordExists) {
    const treeRecord = treeRecords[treeId]
    switch(treeRecord.leavesCollected) {
      case 1:
        icon = <IconTree1 className={treeStyles.treeImage}/>;
        break;
      case 2:
        icon = <IconTree2 className={treeStyles.treeImage}/>;
        break;
      case 3:
        icon = <IconTree3 className={treeStyles.treeImage}/>;
        break;
      case 4:
        icon = <IconTree4 className={treeStyles.treeImage}/>;
        break;
      case 5:
        icon = <IconTree5 className={treeStyles.treeImage}/>;
        break;
      case 6:
        icon = <IconTree6 className={treeStyles.treeImage}/>;
        break;
      case 7:
        icon = <IconTree7 className={treeStyles.treeImage}/>;
        break;
      default:
        icon = <IconTree0 className={treeStyles.treeImage}/>;
    }
    if (treeComplete) {
      bannerContent = <><p><strong>{levellangString}</strong></p><p><strong>Tree started {treeRecord.startDate.slice(0, 10)}</strong></p><p><strong>Tree completed {treeRecord.completionDate.slice(0, 10)}</strong></p><p><strong>Score: {Object.values(treeRecord.leaves).reduce((sum, leaf) => sum + leaf.score, 0)}/35</strong></p></>
    }
    else {
      bannerContent = <><p><strong>{levellangString}</strong></p><p><strong>Tree started {treeRecord.startDate.slice(0, 10)}</strong></p><p><strong>{`${treeRecord.leavesCollected}/7 leaves collected`}</strong></p></>
    }
  } else {
    bannerContent = <><p><strong>{levellangString}</strong></p><p><strong>Tree not started yet</strong></p></>
    icon = <IconTree0 className={treeStyles.treeImage}/>;
  }

  // callback for reset button
  function handleResetButton() {
    stateResetTree(treeId);
  }

  // if page still loading return "loading" component
  if (loading) return <Loading/>;

  // if error just show error message
  if (error) return <div className={pageStyles.styledPage}><div className={pageStyles.alertBanner}> <p>Error downloading tree.</p></div></div>

  return (
    <>
      <Helmet>
        <title>{tree.title} | LingoLeaf</title>
      </Helmet>
      <div className={pageStyles.styledPage}>
        {tree.week_index <= EARLY_CONTENT_WARNING_WEEK && <div className={pageStyles.alertBanner}><strong>Warning:</strong> This tree is from an earlier version of LingoLeaf and its quality of its story, questions etc. might not be quite as good as some of our newer material (ID #{EARLY_CONTENT_WARNING_WEEK + 1} onwards).</div>}
        <h1 lang={tree.language} translate="no" className={`notranslate ${treeStyles.treeTitle}`}>{tree.title}</h1>
        {tree.language === 'ja' && <span lang={tree.language} translate="no" className="notranslate">({tree.title_kana})</span>}
        <p>{tree.tagline}</p>
        <div className={treeStyles.treeImageBox}>
          {icon}
        </div>
        <div className={pageStyles.alertBanner}><strong>{bannerContent}</strong></div>
        <hr className={pageStyles.hrule} />
        <h2>Leaves</h2>
        {tree.leaves.map((leaf, index) => (
          <LeafButton key={index} index={index} tree={tree} leaf={leaf} score={scores[index]} complete={completions[index]}/>
        ))}
        <hr className={pageStyles.hrule} />
        <p>The reset button below will delete all progress on this tree, such as collected leaves and used hints.</p>
        <p>This does not affect your streak.</p>
        <button onClick={handleResetButton} disabled={!recordExists}>Reset Tree</button>
      </div>
    </>
  );
}

export default Tree;